// src/components/.../AutocompleteWrapper.js

import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useField } from 'formik';

import { styled } from '@mui/material/styles';
import { Autocomplete, FormHelperText, InputLabel, TextField } from '@mui/material';

import { useGetCountriesQuery } from '../../../redux/features/countryApi';
import { useGetCurrenciesQuery } from '../../../redux/features/currencyApi';

//----------------------------------- STYLES -----------------------------------//

const TextFieldStyled = styled(TextField)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(-1),
  },

  '& .MuiInputBase-input': {
    borderRadius: 4,

    backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#f5f5f5',

    fontSize: 16,
    fontWeight: 500,

    transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),

    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),

    '&:focus': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

//----------------------------------- UTILS -----------------------------------//

const isEmptyValue = (value) => {
  return value === null || value === undefined || value === '';
};

const getOptionIdentity = (option, fieldName) => {
  if (isEmptyValue(option)) {
    return null;
  }

  if (typeof option !== 'object') {
    return option;
  }

  const isCurrencyField = fieldName === 'currency' || fieldName === 'currencycode' || fieldName === 'currency_code';

  const isCountryField = fieldName.includes('country');

  /*
   * Prefer the business identifier for currency
   * and country fields.
   *
   * This allows a Formik value such as "EUR" or "MD"
   * to match an option object returned by the API.
   */
  if (isCurrencyField) {
    return option.currency_code ?? option.code ?? option.value ?? option.uf ?? option.id ?? null;
  }

  if (isCountryField) {
    return option.value ?? option.code ?? option.uf ?? option.id ?? null;
  }

  return option.id ?? option.uf ?? option.code ?? option.value ?? option.currency_code ?? null;
};

const findOptionByValue = (options, value, fieldName) => {
  if (isEmptyValue(value)) {
    return null;
  }

  const valueIdentity = getOptionIdentity(value, fieldName);

  const matchingOption =
    options.find((option) => {
      const optionIdentity = getOptionIdentity(option, fieldName);

      if (optionIdentity === null || valueIdentity === null) {
        return false;
      }

      return String(optionIdentity) === String(valueIdentity);
    }) ?? null;

  if (matchingOption) {
    return matchingOption;
  }

  /*
   * Preserve an object value when the related option
   * is not currently present in the loaded options.
   */
  if (typeof value === 'object') {
    return value;
  }

  return null;
};

const getPersonLabel = (option) => {
  return [option?.first_name, option?.last_name].filter(Boolean).join(' ');
};

//----------------------------------- MAIN COMPONENT -----------------------------------//

const AutocompleteWrapper = ({
  name,
  label,
  required = false,
  disabled = false,
  personsArray,

  /*
   * Explicit options take priority over Redux
   * and RTK Query data.
   */
  options: providedOptions,

  onBlur: externalOnBlur,
  onChange: externalOnChange,

  sx,
  ...textFieldProps
}) => {
  const [field, meta, helpers] = useField(String(name));

  const fieldName = field.name;

  const hasProvidedOptions = Array.isArray(providedOptions);

  const isCountryField = fieldName.includes('country');

  const isCurrencyField = fieldName === 'currency' || fieldName === 'currencycode' || fieldName === 'currency_code';

  /*
   * Hooks are always called, but skip prevents
   * irrelevant API requests.
   */
  const { data: countries = [], isFetching: isFetchingCountries } = useGetCountriesQuery(undefined, {
    skip: !isCountryField || hasProvidedOptions,
  });

  const { data: currencies = [], isFetching: isFetchingCurrencies } = useGetCurrenciesQuery(undefined, {
    skip: !isCurrencyField || hasProvidedOptions,
  });

  /*
   * Remaining data sources still use legacy Redux.
   * They can be migrated independently.
   */
  const persons = useSelector((state) => state.personSlice?.persons ?? []);

  const statusesLocal = useSelector((state) => state.statusSlice?.statusesLocal ?? []);

  const contacts = useSelector((state) => state.contactSlice?.contacts ?? []);

  const noteTerms = useSelector((state) => state.noteTermSlice?.noteTerms ?? []);

  const contractTerms = useSelector((state) => state.contractTermSlice?.contractTerms ?? []);

  const itemsForItemInv = useSelector((state) => state.itemForItemInvSlice?.itemsForItemInv ?? []);

  const options = useMemo(() => {
    if (hasProvidedOptions) {
      return providedOptions;
    }

    if (fieldName === 'person') {
      return Array.isArray(personsArray) ? personsArray : persons;
    }

    if (fieldName.startsWith('personscontact[')) {
      return persons;
    }

    if (fieldName === 'stype' || fieldName === 'status') {
      return statusesLocal;
    }

    if (isCurrencyField) {
      return currencies;
    }

    if (isCountryField) {
      return countries;
    }

    if (fieldName === 'note_terms' || fieldName === 'payment_term') {
      return noteTerms;
    }

    if (fieldName === 'contract_terms') {
      return contractTerms;
    }

    if (fieldName.startsWith('iteminv_invs')) {
      return itemsForItemInv;
    }

    return contacts;
  }, [
    contacts,
    contractTerms,
    countries,
    currencies,
    fieldName,
    hasProvidedOptions,
    isCountryField,
    isCurrencyField,
    itemsForItemInv,
    noteTerms,
    persons,
    personsArray,
    providedOptions,
    statusesLocal,
  ]);

  const selectedValue = useMemo(() => {
    return findOptionByValue(options, field.value, fieldName);
  }, [field.value, fieldName, options]);

  const getOptionLabel = useCallback(
    (rawOption) => {
      const option = findOptionByValue(options, rawOption, fieldName) ?? rawOption;

      if (isEmptyValue(option)) {
        return '';
      }

      if (typeof option !== 'object') {
        return String(option);
      }

      if (fieldName === 'person' || fieldName.startsWith('personscontact[')) {
        return getPersonLabel(option);
      }

      if (fieldName === 'stype') {
        return String(option.st ?? '');
      }

      if (fieldName === 'htype') {
        return String(option.ht ?? '');
      }

      if (fieldName === 'mtype' || fieldName === 'mode') {
        return String(option.mt ?? '');
      }

      if (isCurrencyField) {
        return String(option.currency_code ?? option.code ?? option.value ?? '');
      }

      if (fieldName === 'status') {
        return String(option.type ?? '');
      }

      if (isCountryField) {
        return String(option.label ?? option.value ?? option.code ?? '');
      }

      if (fieldName === 'note_terms' || fieldName === 'payment_term') {
        return String(option.note_short ?? '');
      }

      if (fieldName === 'contract_terms') {
        return String(option.term_short ?? '');
      }

      if (fieldName.startsWith('iteminv_invs')) {
        return String(option.description ?? '');
      }

      if (fieldName === 'lang') {
        return String(option.lang ?? option.label ?? option.value ?? '');
      }

      return String(option.companyname ?? option.label ?? option.name ?? option.value ?? '');
    },
    [fieldName, isCountryField, isCurrencyField, options]
  );

  const isOptionEqualToValue = useCallback(
    (option, value) => {
      const optionIdentity = getOptionIdentity(option, fieldName);

      const valueIdentity = getOptionIdentity(value, fieldName);

      if (optionIdentity === null || valueIdentity === null) {
        return option === value;
      }

      return String(optionIdentity) === String(valueIdentity);
    },
    [fieldName]
  );

  const handleChange = useCallback(
    (event, value, reason, details) => {
      helpers.setValue(value);

      externalOnChange?.(event, value, reason, details);
    },
    [externalOnChange, helpers]
  );

  const handleBlur = useCallback(
    (event) => {
      helpers.setTouched(true);

      externalOnBlur?.(event);
    },
    [externalOnBlur, helpers]
  );

  const hasError = Boolean(meta.touched && meta.error);

  const helperText = hasError && typeof meta.error === 'string' ? meta.error : '';

  const showHelperText = !fieldName.startsWith('iteminv_invs');

  const isLoadingOptions = (isCountryField && isFetchingCountries) || (isCurrencyField && isFetchingCurrencies);

  return (
    <Autocomplete
      disabled={disabled}
      loading={isLoadingOptions}
      options={options}
      value={selectedValue}
      onChange={handleChange}
      onBlur={handleBlur}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderInput={(params) => (
        <>
          <InputLabel
            htmlFor={fieldName}
            label={label}
            required={required}
            shrink="true"
            sx={{
              fontSize: 18,
              fontWeight: 'bold',
            }}
          >
            {label}
          </InputLabel>

          <TextFieldStyled
            {...params}
            {...textFieldProps}
            id={fieldName}
            name={fieldName}
            fullWidth
            size="small"
            required={required}
            error={hasError}
            autoComplete="off"
            sx={{
              backgroundColor: '#f5f5f5',

              ...sx,
            }}
            inputProps={{
              ...params.inputProps,
              id: fieldName,
              autoComplete: 'new-password',
            }}
          />

          {showHelperText && (
            <FormHelperText
              error={hasError}
              sx={{
                color: hasError ? 'error.main' : undefined,

                fontSize: 14,
                width: '100%',
              }}
            >
              {helperText}
            </FormHelperText>
          )}
        </>
      )}
    />
  );
};

export default AutocompleteWrapper;
